/** DV360 reports VCR as completions ÷ impressions; other platforms use starts. */
const IMPRESSIONS_BASED_VIDEO_COMPLETION_SOURCES = new Set(['dv_360', 'google_dv360']);

type VideoCompletionInputs = {
  completes: number;
  starts: number;
  impressions?: number;
  source?: string | null;
};

const usesImpressionsForVideoCompletionRate = (source?: string | null): boolean =>
  IMPRESSIONS_BASED_VIDEO_COMPLETION_SOURCES.has(source ?? '');

const videoCompletionDenominator = ({
  starts,
  impressions = 0,
  source,
}: Pick<VideoCompletionInputs, 'starts' | 'impressions' | 'source'>): number => {
  if (usesImpressionsForVideoCompletionRate(source)) {
    return impressions;
  }
  return starts;
};

/** Returns 0–100. */
const calculateVideoCompletionRatePercent = ({
  completes,
  starts,
  impressions = 0,
  source,
}: VideoCompletionInputs): number => {
  const denominator = videoCompletionDenominator({ starts, impressions, source });
  if (denominator === 0) return 0;
  return (completes / denominator) * 100;
};

/** Returns 0–1 (for breakdown tables that multiply by 100 when formatting). */
const calculateVideoCompletionRateRatio = (inputs: VideoCompletionInputs): number =>
  calculateVideoCompletionRatePercent(inputs) / 100;

/**
 * Period-over-period point difference in percentage points.
 * ``dif*`` values are (current − past), matching reporting ``dif`` payloads.
 */
const calculateVideoCompletionRateDifference = ({
  completes,
  starts,
  impressions = 0,
  difCompletes,
  difStarts,
  difImpressions = 0,
  source,
}: VideoCompletionInputs & {
  difCompletes: number;
  difStarts: number;
  difImpressions?: number;
}): number => {
  const currentRate = calculateVideoCompletionRatePercent({
    completes,
    starts,
    impressions,
    source,
  });

  const previousCompletes = completes - difCompletes;
  const previousStarts = starts - difStarts;
  const previousImpressions = impressions - difImpressions;
  const previousRate = calculateVideoCompletionRatePercent({
    completes: previousCompletes,
    starts: previousStarts,
    impressions: previousImpressions,
    source,
  });

  return currentRate - previousRate;
};

const calculateRate = (starts: number, completes: number) => {
  if (starts === 0) return 0;
  return (completes / starts) * 100;
};

const calculateRateDifference = (starts: number, completes: number, difStarts: number, difCompletes: number) => {
  if (starts === 0) return 0;
  return (completes / starts) * 100 - (difCompletes / difStarts) * 100;
};

const calculatePercentChange = (current: number, past: number) => {
  if (current === 0) return 0;
  return ((current - past) / past) * 100;
};

export {
  usesImpressionsForVideoCompletionRate,
  calculateVideoCompletionRatePercent,
  calculateVideoCompletionRateRatio,
  calculateVideoCompletionRateDifference,
  calculateRate,
  calculateRateDifference,
  calculatePercentChange,
};
